import { useState, useMemo, useCallback } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { useWithdrawFromPayoutAccountMutation } from '@graphql/generated';
import { PayoutStatus } from './PayoutDialog';
import { PayoutAccountInfo } from '../CreditCardSetUp.context';
import { useCreditCardSetupTelemetry } from '../CreditCardSetUp.telemetry';

export const usePayoutDialogController = (fetchPayoutInfo: () => void, payoutAccountInfo: PayoutAccountInfo | null, eventId: string) => {
  const telemetry = useCreditCardSetupTelemetry();
  const [status, setStatus] = useState<PayoutStatus>('pending');

  const payoutAccountAllowed = useMemo(() => Boolean(payoutAccountInfo?.balance && payoutAccountInfo.balance.available.valueInMajorUnits > 2), [payoutAccountInfo]);
  const { isOpen: isOpenPayoutDialog, onOpen: onOpenPayoutDialog, onClose } = useDisclosure();
  const [withdrawBalance, { loading: loadingPayoutRequest }] = useWithdrawFromPayoutAccountMutation({
    variables: {
      eventId,
    },
    onCompleted: response => {
      if (response.withdrawFromPayoutAccount.status !== 'failed') {
        telemetry.payoutDialog.enter(response.withdrawFromPayoutAccount.status);
        fetchPayoutInfo();
        setStatus('success');
      } else {
        telemetry.payoutDialog.enter(response.withdrawFromPayoutAccount.status);
        setStatus('error');
      }
    },
    onError: () => {
      telemetry.payoutDialog.enter('error');
      setStatus('error');
    },
  });

  const onCloseDialog = useCallback(() => {
    setStatus('pending');
    onClose();
  }, [setStatus, onClose]);

  const resetPayoutStatus = useCallback(() => {
    setStatus('pending');
  }, []);

  return {
    withdrawBalance,
    loadingPayoutRequest,
    payoutAccountAllowed,
    isOpenPayoutDialog,
    onOpenPayoutDialog,
    onClosePayoutDialog: onCloseDialog,
    resetPayoutStatus,
    payoutStatus: status,
    accountNumber: payoutAccountInfo?.externalAccountLast4 || '',
  };
};
